import {
  updateResourcePlanSummaryTabCountCache,
  updateResourceRequestsCache,
  updateResourcePlansCache
} from '~/modules/resourcing/common/util/resourceRequestUtil.js';
import { updateCache as draftUpdateCache } from '~/modules/resourcing/common/enhancers/requestStatusHandlers/withMarkResourceRequestAsDraft';
import { updateHiringStatusSummaryCountCache } from '~/modules/resourcing/common/util/hiringStatusUtil';
import { updateCacheForResourcingPlan } from '~/modules/resourcing/common/hooks/useMarkToBeHiredResourceRequestWithHiringRequestedSubStatus';
import { updateCache as updateDeleteCache } from '~/modules/resourcing/common/hooks/useDeleteResourceRequestHandler';
import { ResourceRequestStatus } from '~/types';

export const getCacheUpdateFunctions = ({
  request,
  hiringStatusSummaryCountQueryVariables,
  resourceRequestsQueryVariables,
  chartTabContext,
  filter,
  summaryCountPlanFilter,
  isHiringStatusSubFilterCacheUpdateEnabled,
  isPtiEnabled
}) => () => {
  const resourcePlanFilter =
    Object.keys(resourceRequestsQueryVariables?.filter || []).length !== 0
      ? resourceRequestsQueryVariables.filter
      : null;

  return {
    getDefaultCacheFunctions: ({
      resourceRequest,
      responseType,
      requestStatus
    }) => [
      updateResourceRequestsCache({
        projectId: resourceRequest.projectUri,
        responseType
      }),
      updateResourcePlanSummaryTabCountCache({
        responseType,
        newRequestStatus: requestStatus,
        resourceRequest,
        planFilter: summaryCountPlanFilter
      })
    ],
    onResourceRequestSubmitCacheFunctions: [
      updateResourcePlanSummaryTabCountCache({
        responseType: 'markResourceRequestAsSubmitted2',
        newRequestStatus: ResourceRequestStatus.Submitted,
        resourceRequest: request,
        planFilter: summaryCountPlanFilter
      }),
      updateResourcePlansCache({
        existingResourceRequest: request,
        newRequestStatus: ResourceRequestStatus.Submitted,
        requestAccessLevel: resourceRequestsQueryVariables?.requestAccessLevel,
        planFilter: resourcePlanFilter,
        chartSelectedTab: chartTabContext?.selectedChartTab
      }),
      ...(isHiringStatusSubFilterCacheUpdateEnabled
        ? [
            updateHiringStatusSummaryCountCache({
              responseType: 'markResourceRequestAsSubmitted2',
              newRequestStatus: ResourceRequestStatus.Submitted,
              oldHiringStatus: request.sourceMetadata?.hiringStatus,
              existingRequestStatus: request.requestStatus,
              hiringStatusSummaryCountQueryVariables
            })
          ]
        : [])
    ],
    onResourceRequestRejectCacheFunctions: [
      updateResourcePlanSummaryTabCountCache({
        responseType: 'markResourceRequestAsRejected2',
        newRequestStatus: ResourceRequestStatus.Rejected,
        resourceRequest: request,
        planFilter: summaryCountPlanFilter
      }),
      updateResourcePlansCache({
        existingResourceRequest: request,
        newRequestStatus: ResourceRequestStatus.Rejected,
        requestAccessLevel: resourceRequestsQueryVariables?.requestAccessLevel,
        planFilter: resourcePlanFilter,
        chartSelectedTab: chartTabContext?.selectedChartTab
      }),
      ...(isHiringStatusSubFilterCacheUpdateEnabled
        ? [
            updateHiringStatusSummaryCountCache({
              responseType: 'markResourceRequestAsRejected2',
              newRequestStatus: ResourceRequestStatus.Rejected,
              oldHiringStatus: request.sourceMetadata?.hiringStatus,
              existingRequestStatus: request.requestStatus,
              hiringStatusSummaryCountQueryVariables
            })
          ]
        : [])
    ],
    onResourceRequestUnsubmitCacheFunctions: [
      draftUpdateCache(null, request.projectUri),
      updateResourcePlanSummaryTabCountCache({
        responseType: 'markResourceRequestAsDraft2',
        newRequestStatus: ResourceRequestStatus.Draft,
        resourceRequest: request,
        planFilter: summaryCountPlanFilter
      }),
      updateResourcePlansCache({
        existingResourceRequest: request,
        newRequestStatus: ResourceRequestStatus.Draft,
        requestAccessLevel: resourceRequestsQueryVariables?.requestAccessLevel,
        planFilter: resourcePlanFilter,
        chartSelectedTab: chartTabContext?.selectedChartTab
      }),
      ...(isHiringStatusSubFilterCacheUpdateEnabled
        ? [
            updateHiringStatusSummaryCountCache({
              responseType: 'markResourceRequestAsDraft2',
              newRequestStatus: ResourceRequestStatus.Draft,
              oldHiringStatus: request.sourceMetadata?.hiringStatus,
              existingRequestStatus: request.requestStatus,
              hiringStatusSummaryCountQueryVariables
            })
          ]
        : [])
    ],
    onResourceRequestDeleteCacheFunctions: [
      updateDeleteCache({
        projectUri: request.projectUri,
        resourceRequestsQueryVariables: resourceRequestsQueryVariables
          ? {
              ...resourceRequestsQueryVariables,
              projectUri: request.projectUri
            }
          : null,
        request,
        filter
      }),
      updateResourcePlanSummaryTabCountCache({
        responseType: 'deleteResourceRequest2',
        newRequestStatus: null,
        resourceRequest: request,
        planFilter: summaryCountPlanFilter
      }),
      updateResourcePlansCache({
        existingResourceRequest: request,
        newRequestStatus: null,
        requestAccessLevel: resourceRequestsQueryVariables?.requestAccessLevel,
        planFilter: resourcePlanFilter,
        chartSelectedTab: chartTabContext?.selectedChartTab
      }),
      ...(isHiringStatusSubFilterCacheUpdateEnabled
        ? [
            updateHiringStatusSummaryCountCache({
              responseType: 'deleteResourceRequest2',
              newRequestStatus: null,
              oldHiringStatus: request.sourceMetadata?.hiringStatus,
              existingRequestStatus: request.requestStatus,
              hiringStatusSummaryCountQueryVariables
            })
          ]
        : [])
    ],
    onResourceRequestUpdateHiringStatusCacheFunctions: isHiringStatusSubFilterCacheUpdateEnabled
      ? [
          updateCacheForResourcingPlan({
            resourcePlanSubstatusFilter:
              chartTabContext?.summaryFilterState?.quickFilter?.filterByAccess,
            resourceRequestsQueryVariables
          }),
          updateHiringStatusSummaryCountCache({
            responseType:
              'markToBeHiredResourceRequestWithHiringRequestedSubStatus',
            newRequestStatus: ResourceRequestStatus.Tobehired,
            oldHiringStatus: request.sourceMetadata?.hiringStatus,
            existingRequestStatus: request.requestStatus,
            hiringStatusSummaryCountQueryVariables
          })
        ]
      : [],
    onResourceRequestAcceptCacheFunctions: [
      updateResourcePlanSummaryTabCountCache({
        responseType: 'markResourceRequestAsComplete2',
        newRequestStatus: ResourceRequestStatus.Complete,
        resourceRequest: request,
        planFilter: summaryCountPlanFilter
      }),
      updateResourcePlansCache({
        existingResourceRequest: request,
        newRequestStatus: ResourceRequestStatus.Complete,
        requestAccessLevel: resourceRequestsQueryVariables?.requestAccessLevel,
        planFilter: resourcePlanFilter,
        chartSelectedTab: chartTabContext?.selectedChartTab
      })
    ],
    onResourceRequestToBeHiredCacheFunctions: [
      updateResourcePlanSummaryTabCountCache({
        responseType: 'markResourceRequestAsToBeHired2',
        newRequestStatus: ResourceRequestStatus.Tobehired,
        resourceRequest: request,
        planFilter: summaryCountPlanFilter
      }),
      updateResourcePlansCache({
        existingResourceRequest: request,
        newRequestStatus: ResourceRequestStatus.Tobehired,
        requestAccessLevel: resourceRequestsQueryVariables?.requestAccessLevel,
        planFilter: resourcePlanFilter,
        chartSelectedTab: chartTabContext?.selectedChartTab
      }),
      ...(isPtiEnabled
        ? [
            updateHiringStatusSummaryCountCache({
              responseType: 'markResourceRequestAsToBeHired2',
              newRequestStatus: ResourceRequestStatus.Tobehired,
              oldHiringStatus: request.sourceMetadata?.hiringStatus,
              existingRequestStatus: request.requestStatus,
              hiringStatusSummaryCountQueryVariables
            })
          ]
        : [])
    ]
  };
};
